// src/context/AuthContext.tsx

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { collection, doc, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import type { UserRole } from '@/config/routes.config';
import type { ViewId } from '@/services/userViews.service';
import { userViewsService } from '@/services/userViews.service';

export interface SGCUser {
  uid: string;
  email: string;
  fullName: string;
  nomina: string;
  roleSGC: UserRole;
  accesoSGC: boolean;
  photoUrl?: string | null;
  allowedViews?: ViewId[];
}

interface AuthContextType {
  user: SGCUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  hasViewAccess: (viewId: ViewId) => boolean;
  refreshUserViews: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] =         useState<SGCUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Guarda el unsubscribe del onSnapshot activo para poder cancelarlo
  const profileUnsubRef = useRef<(() => void) | null>(null);
  const viewsUnsubRef = useRef<(() => void) | null>(null);
  const roleDefaultsUnsubRef = useRef<(() => void) | null>(null);

  // Función para verificar si el usuario tiene acceso a una vista
  const hasViewAccess = useCallback((viewId: ViewId): boolean => {
    if (!user) return false;
    
    // Si no tiene vistas configuradas, usar las vistas por defecto según el rol
    if (!user.allowedViews || user.allowedViews.length === 0) {
      const defaultViews = userViewsService.getDefaultViewsForRole(user.roleSGC);
      return defaultViews.includes(viewId);
    }
    
    return user.allowedViews.includes(viewId);
  }, [user]);

  // Función para refrescar las vistas del usuario
  const refreshUserViews = useCallback(async () => {
    if (!user) return;
    
    try {
      const permissions = await userViewsService.getUserPermissions(user.uid);
      if (permissions) {
        setUser((prev) => prev ? { ...prev, allowedViews: permissions.allowedViews } : prev);
      }
    } catch (error) {
      console.error('Error refreshing user views:', error);
    }
  }, [user?.uid]);

  useEffect(() => {
    // Escucha cambios en la sesión de Firebase Auth
    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      // Cancelar los listeners anteriores si existen
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }
      if (viewsUnsubRef.current) {
        viewsUnsubRef.current();
        viewsUnsubRef.current = null;
      }

      if (!firebaseUser) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Suscribirse en tiempo real al documento del usuario en Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const unsubProfile = onSnapshot(
        userDocRef,
        (snap) => {
          if (!snap.exists()) {
            // El documento fue eliminado
            signOut(auth);
            setUser(null);
            setIsLoading(false);
            return;
          }

          const data = snap.data();
          const accesoSGC = data.accesoSGC === true;

          if (!accesoSGC) {
            // Le quitaron el acceso: cerrar sesión automáticamente
            signOut(auth);
            setUser(null);
            setIsLoading(false);
            return;
          }

          // Actualizar el perfil (incluye cambios de rol en tiempo real)
          setUser((prev) => ({
            uid:       firebaseUser.uid,
            email:     data.email     ?? '',
            fullName:  data.fullName  ?? '',
            nomina:    data.nomina    ?? '',
            roleSGC:   data.roleSGC   as UserRole,
            accesoSGC: true,
            photoUrl:  prev?.photoUrl ?? null,
            allowedViews: prev?.allowedViews ?? [],
          }));
          setIsLoading(false);
        },
        () => {
          // Error de permiso u otro error de Firestore
          setUser(null);
          setIsLoading(false);
        }
      );

      profileUnsubRef.current = unsubProfile;

      // Suscribirse a cambios en los permisos de vistas (tiempo real)
      const unsubViews = userViewsService.subscribeToUserPermissions(
        firebaseUser.uid,
        (permissions) => {
          setUser((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              allowedViews: permissions?.allowedViews ?? [],
            };
          });
        }
      );

      viewsUnsubRef.current = unsubViews;
    });

    return () => {
      unsubAuth();
      if (profileUnsubRef.current) profileUnsubRef.current();
      if (viewsUnsubRef.current) viewsUnsubRef.current();
    };
  }, []);

  // Suscribirse a cambios en los defaults de rol (para que hasViewAccess siempre use los actualizados)
  useEffect(() => {
    const unsub = userViewsService.subscribeToRoleDefaults(() => {
      // La cache del servicio se actualiza internamente; forzar re-render
      setUser((prev) => prev ? { ...prev } : prev);
    });
    roleDefaultsUnsubRef.current = unsub;
    return () => unsub();
  }, []);

  useEffect(() => {
    let active = true;

    const loadPhoto = async () => {
      if (!user?.nomina) return;

      try {
        const raw = user.nomina.trim();
        if (!raw) return;

        const parsed = parseInt(raw, 10);
        let foto: string | null = null;

        if (!isNaN(parsed)) {
          const byNumber = await getDocs(
            query(collection(db, 'UsuariosDcc'), where('no', '==', parsed), limit(1))
          );
          if (!byNumber.empty) foto = (byNumber.docs[0].data().foto as string) ?? null;
        }

        if (!foto) {
          const byString = await getDocs(
            query(collection(db, 'UsuariosDcc'), where('no', '==', raw), limit(1))
          );
          if (!byString.empty) foto = (byString.docs[0].data().foto as string) ?? null;
        }

        if (active) {
          setUser((prev) => (prev ? { ...prev, photoUrl: foto } : prev));
        }
      } catch {
        if (active) {
          setUser((prev) => (prev ? { ...prev, photoUrl: null } : prev));
        }
      }
    };

    void loadPhoto();

    return () => {
      active = false;
    };
  }, [user?.nomina]);

  const login = useCallback(async (email: string, password: string) => {
    // El onSnapshot se activa solo al hacer signIn; aquí solo validamos
    // que el usuario tenga acceso antes de dejar que el listener tome el control.
    const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);

    // Lectura rápida para dar feedback inmediato de error si no tiene acceso
    const { getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));

    if (!snap.exists() || snap.data().accesoSGC !== true) {
      await signOut(auth);
      throw new Error(
        !snap.exists()
          ? 'Usuario no encontrado en el sistema.'
          : 'No tienes acceso al SGC. Contacta al administrador.'
      );
    }
    // El onSnapshot ya se encargará de setUser con los datos completos
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    const { user: firebaseUser } = await signInWithPopup(auth, provider);

    const { getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));

    if (!snap.exists() || snap.data().accesoSGC !== true) {
      await signOut(auth);
      throw new Error(
        !snap.exists()
          ? 'Tu cuenta de Google no está registrada en el sistema.'
          : 'No tienes acceso al SGC. Contacta al administrador.'
      );
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      loginWithGoogle,
      logout,
      hasViewAccess,
      refreshUserViews,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
